// DS3.6
import React, { useCallback, useState } from 'react';
import uniq from 'lodash/uniq';
import { useTranslation } from 'react-i18next';
import DSTagInput, {
  TagInputProps,
} from '@wix/da-ds/pkg/formControls/dsPresets/TagInput';
import CopyIcon from '@wix/da-ds/pkg/Icons/16x16/Copy';
import CheckIcon from '@wix/da-ds/pkg/Icons/16x16/Check';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import type { RequestStatus } from './redux/types';

import s from './TagInput.scss';

export interface Props
  extends Omit<
    TagInputProps,
    'value' | 'tags' | 'onAddTag' | 'onRemoveTag' | 'onChange' | 'onCancel'
  > {
  searchRequestStatus?: RequestStatus;
  searchRequest: (search: string) => void;

  value?: string[];
  onChange?: (value: string[]) => void;
  withCopy?: boolean;
}

const TagInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      value: tags = [],
      onChange,
      searchRequestStatus,
      searchRequest,
      tagSuggestions,
      maxLength,
      withCopy,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [value, setValue] = useState('');
    const [copied, setCopied] = useState(false);

    const handleTagChange = useCallback(
      (newTags: string[]) => {
        onChange && onChange(newTags);
        setCopied(false);
      },
      [onChange]
    );
    const handleAddTag = useCallback(
      tagToAdd => {
        handleTagChange(uniq([...tags, tagToAdd.replace(/^#/, '')]));
        setValue('');
      },
      [handleTagChange, tags]
    );
    const handleAddTags = useCallback(
      tagsToAdd => {
        handleTagChange(
          uniq([...tags, ...tagsToAdd.map(t => t.replace(/^#/, ''))])
        );
        setValue('');
      },
      [handleTagChange, tags]
    );

    const handleChange = useCallback(
      e => {
        const inputValue = e.target.value;
        setValue(inputValue.substring(0, maxLength));
        setCopied(false);
        searchRequest(inputValue.replace(/^#/, ''));
      },
      [maxLength, searchRequest]
    );
    const handleCopy = useCallback(
      e => {
        if (document) {
          const el = document.createElement('input');
          el.setAttribute('type', 'text');
          el.value = tags.join(' ');

          document.body.appendChild(el);
          el.select();
          el.setSelectionRange(0, 1048576);
          document.execCommand('copy');

          document.body.removeChild(el);
        }
        setCopied(true);
      },
      [tags]
    );

    return (
      <DSTagInput
        value={value}
        onChange={handleChange}
        onCancel={() => setValue('')}
        onAddTag={handleAddTag}
        onAddTags={handleAddTags}
        onRemoveTag={tagToRemove =>
          handleTagChange(tags.filter(tag => tag !== tagToRemove))
        }
        tags={tags}
        tagSuggestions={
          value === ''
            ? []
            : tagSuggestions.filter(suggestion => !tags.includes(suggestion))
        }
        valueAsFirstSuggestion
        maxLength={maxLength}
        postfix={
          withCopy && (
            <IconButton
              size="small"
              className={s['copy-button']}
              noPaddings
              icon={copied ? CheckIcon : CopyIcon}
              tooltipText={
                copied
                  ? t('common.tagInput.copy.tooltip.done')
                  : t('common.tagInput.copy.tooltip')
              }
              aria-label={
                copied
                  ? t('common.tagInput.copy.tooltip.done')
                  : t('common.tagInput.copy.tooltip')
              }
              onClick={copied ? undefined : handleCopy}
            />
          )
        }
        {...rest}
        ref={ref}
      />
    );
  }
);
TagInput.displayName = 'TagInput';

export default TagInput;
